import { useState } from "react";
import { motion } from "framer-motion";
import {
  BookOpen,
  Heart,
  HandHeart,
  ChevronDown,
  Copy,
  Check,
  ArrowRight,
  Sparkles,
  GraduationCap,
} from "lucide-react";
import { formations, inscriptionEtapes, type Formation } from "@/data/formations";
import { AudioPlayer } from "@/components/AudioPlayer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const serif = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

const accentMap = {
  indigo: { icon: BookOpen, ring: "from-primary/15 to-primary/5", chip: "bg-primary/10 text-primary" },
  ambre: { icon: Heart, ring: "from-accent/20 to-accent/5", chip: "bg-accent/15 text-accent-foreground" },
  vert: { icon: HandHeart, ring: "from-chart-3/20 to-chart-3/5", chip: "bg-chart-3/15 text-chart-3" },
} as const;

function CodeBadge({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(code);
    setCopied(true);
    toast.success(`Code « ${code} » copié`);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="group inline-flex items-center gap-2 rounded-lg border border-accent/40 bg-accent/5 px-3 py-1.5 transition-colors hover:bg-accent/15"
      aria-label={`Copier le code ${code}`}
    >
      <span className="text-xs text-muted-foreground">Code :</span>
      <span className="font-mono text-sm font-medium tracking-wider text-foreground">{code}</span>
      {copied ? (
        <Check className="h-3.5 w-3.5 text-chart-3" />
      ) : (
        <Copy className="h-3.5 w-3.5 text-muted-foreground group-hover:text-accent-foreground" />
      )}
    </button>
  );
}

function ChapitreItem({
  ch,
  index,
  open,
  onToggle,
}: {
  ch: Formation["chapitres"][number];
  index: number;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-muted/50"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent/15 font-mono text-sm font-medium text-accent-foreground">
          {index + 1}
        </span>
        <span className="flex-1 font-medium text-foreground">{ch.titre}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        className="grid transition-all duration-300 ease-in-out"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <div className="space-y-4 px-4 pb-4 pt-1">
            <p className="text-[15px] leading-relaxed text-muted-foreground">{ch.texte}</p>
            <AudioPlayer src={ch.audio} label={ch.titre} />
          </div>
        </div>
      </div>
    </div>
  );
}

function FormationCard({ f, idx }: { f: Formation; idx: number }) {
  const [openCh, setOpenCh] = useState<string | null>(null);
  const cfg = accentMap[f.accent];
  const Icon = cfg.icon;

  return (
    <motion.section
      id={f.id}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ type: "spring", stiffness: 200, damping: 40, delay: idx * 0.05 }}
      className="scroll-mt-24 overflow-hidden rounded-2xl border border-border bg-card shadow-[0_8px_30px_-10px_color-mix(in_srgb,var(--primary)_18%,transparent)]"
    >
      <div className={`bg-gradient-to-br ${cfg.ring} px-6 py-7 sm:px-8`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-background/80 shadow-sm">
              <Icon className="h-7 w-7 text-primary" />
            </div>
            <div>
              <h3 className="text-2xl font-semibold leading-tight text-foreground sm:text-3xl" style={serif}>
                {f.titre}
              </h3>
              <span className={`mt-2 inline-block rounded-full px-3 py-1 text-xs font-medium ${cfg.chip}`}>
                {f.publicCible}
              </span>
            </div>
          </div>
          <CodeBadge code={f.code} />
        </div>
        <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">{f.description}</p>
      </div>

      <div className="space-y-2.5 px-6 py-6 sm:px-8">
        <p className="mb-3 flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Sparkles className="h-4 w-4 text-accent" />
          {f.chapitres.length} mini-cours · texte + narration audio
        </p>
        {f.chapitres.map((ch, i) => (
          <ChapitreItem
            key={ch.id}
            ch={ch}
            index={i}
            open={openCh === ch.id}
            onToggle={() => setOpenCh(openCh === ch.id ? null : ch.id)}
          />
        ))}
        <div className="flex flex-wrap items-center gap-3 pt-3">
          <Button asChild size="lg">
            <a href={f.classroomUrl} target="_blank" rel="noopener noreferrer">
              Rejoindre ce cours
              <ArrowRight className="ml-1 h-4 w-4" />
            </a>
          </Button>
          <span className="text-xs text-muted-foreground">
            Code à saisir dans Google Classroom : <span className="font-mono">{f.code}</span>
          </span>
        </div>
      </div>
    </motion.section>
  );
}

export default function Index() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/90 backdrop-blur-sm">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <a href="#top" className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <GraduationCap className="h-5 w-5" />
            </div>
            <span className="text-lg font-semibold tracking-tight">MIREB · Formations</span>
          </a>
          <nav className="hidden items-center gap-6 text-sm font-medium text-muted-foreground sm:flex">
            <a href="#formations" className="transition-colors hover:text-foreground">Nos formations</a>
            <a href="#inscription" className="transition-colors hover:text-foreground">Comment s'inscrire</a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10" />
        <div className="container relative mx-auto px-4 py-20 text-center sm:py-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 40 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-4 py-1.5 text-sm font-medium text-accent-foreground">
              <Sparkles className="h-4 w-4" />
              3 formations disponibles sur Google Classroom
            </span>
            <h1
              className="mx-auto mt-6 max-w-3xl text-4xl font-semibold leading-[1.1] text-foreground sm:text-6xl"
              style={serif}
            >
              Grandir dans la foi, servir avec amour
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground">
              Découvrez les formations MIREB : un petit cours pour chaque titre, avec sa narration
              audio. Rejoignez directement chaque cours sur Google Classroom avec le code correspondant.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button asChild size="lg">
                <a href="#formations">
                  Voir les formations
                  <ArrowRight className="ml-1 h-4 w-4" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="#inscription">Comment rejoindre ?</a>
              </Button>
            </div>
            <p className="mx-auto mt-10 max-w-xl text-base italic text-muted-foreground" style={serif}>
              « Alors ta lumière poindra comme l'aurore… » — Ésaïe 58:8
            </p>
          </motion.div>
        </div>
      </section>

      {/* Formations */}
      <section id="formations" className="container mx-auto scroll-mt-20 px-4 py-16 sm:py-20">
        <div className="mx-auto mb-12 max-w-2xl text-center">
          <h2 className="text-3xl font-semibold text-foreground sm:text-4xl" style={serif}>
            Nos 3 formations
          </h2>
          <p className="mt-3 text-muted-foreground">
            Cliquez sur chaque titre pour lire le mini-cours et écouter sa narration audio.
          </p>
        </div>
        <div className="mx-auto flex max-w-4xl flex-col gap-10">
          {formations.map((f, i) => (
            <FormationCard key={f.id} f={f} idx={i} />
          ))}
        </div>
      </section>

      {/* Inscription */}
      <section id="inscription" className="scroll-mt-20 bg-muted/40 py-16 sm:py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <h2 className="text-3xl font-semibold text-foreground sm:text-4xl" style={serif}>
              Comment rejoindre une formation ?
            </h2>
            <p className="mt-3 text-muted-foreground">
              Quatre étapes simples pour vous inscrire via Google Classroom.
            </p>
          </div>
          <div className="mx-auto grid max-w-5xl gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {inscriptionEtapes.map((e, i) => (
              <motion.div
                key={e.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 220, damping: 35, delay: i * 0.06 }}
                className="relative rounded-2xl border border-border bg-card p-6 shadow-sm"
              >
                <span
                  className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-lg font-semibold text-primary-foreground"
                  style={serif}
                >
                  {e.num}
                </span>
                <h3 className="mt-4 text-lg font-semibold text-foreground">{e.titre}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{e.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mx-auto mt-10 max-w-5xl rounded-2xl border border-accent/30 bg-accent/5 px-6 py-5 text-center">
            <p className="text-sm text-muted-foreground">
              Rappel des codes —{" "}
              {formations.map((f, i) => (
                <span key={f.id}>
                  {i > 0 && " · "}
                  <span className="font-medium text-foreground">{f.titre.replace(" MIREB", "")}</span>{" "}
                  <span className="font-mono text-accent-foreground">{f.code}</span>
                </span>
              ))}
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-background">
        <div className="container mx-auto px-4 py-10 text-center">
          <div className="flex items-center justify-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <GraduationCap className="h-4 w-4" />
            </div>
            <span className="font-semibold">MIREB</span>
          </div>
          <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
            Mouvement à double mission évangélique et humanitaire, depuis 2014.
          </p>
          <p className="mt-4 text-xs text-muted-foreground">
            © {new Date().getFullYear()} MIREB · Formations en ligne via Google Classroom
          </p>
        </div>
      </footer>
    </div>
  );
}
